import { useMemo, useState } from "react";
import { Button, Input, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";

import type {
  CorrectionReasonRow,
  TransactionRow,
} from "../api/client";
import { formatCents, formatCompactDateTime, formatFullIso } from "../utils/dateFormat";
import { TX_VERB_META, parseTxComment } from "../utils/txComment";
import type { PendingBatch } from "../hooks/usePendingBatch";

const { Text } = Typography;

interface Props {
  transactions: TransactionRow[];
  reasons: CorrectionReasonRow[];
  pending: PendingBatch;
}

/**
 * Per-user transaction history table with ghost ``Correct…`` affordance.
 *
 * Conventions per `feedback-admin-table-baseline`: sticky header, every
 * column sortable + filterable, compact timestamps. Plus the spec-only
 * goodies: client-side text filter, "hide > 1y old" toggle, badges
 * parsed from comments.
 */
export const UserTransactionsList = ({
  transactions,
  reasons,
  pending,
}: Props) => {
  const [filterText, setFilterText] = useState("");
  const [hideOld, setHideOld] = useState(false);

  const filtered = useMemo(() => {
    const ql = filterText.trim().toLowerCase();
    const cutoff = Date.now() - 365 * 24 * 60 * 60 * 1000;
    return transactions.filter((t) => {
      if (hideOld) {
        const created = new Date(t.created).getTime();
        if (!Number.isNaN(created) && created < cutoff) return false;
      }
      if (!ql) return true;
      return (
        (t.article_name ?? "").toLowerCase().includes(ql) ||
        (t.comment ?? "").toLowerCase().includes(ql)
      );
    });
  }, [transactions, filterText, hideOld]);

  const columns: ColumnsType<TransactionRow> = [
    {
      title: "When",
      dataIndex: "created",
      key: "created",
      sorter: (a, b) => +new Date(a.created) - +new Date(b.created),
      defaultSortOrder: "descend",
      render: (v: string) => (
        <Tooltip title={formatFullIso(v)}>
          {formatCompactDateTime(v)}
        </Tooltip>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      sorter: (a, b) => a.amount - b.amount,
      render: (v: number, row) => {
        const text = formatCents(v);
        if (row.corrected_at_correction_id) {
          return (
            <Text delete type="secondary">
              {text}
            </Text>
          );
        }
        return <Text type={v < 0 ? "danger" : "success"}>{text}</Text>;
      },
    },
    {
      title: "Article",
      dataIndex: "article_name",
      key: "article_name",
      sorter: (a, b) =>
        (a.article_name ?? "").localeCompare(b.article_name ?? ""),
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "Note",
      dataIndex: "comment",
      key: "comment",
      render: (v: string | null) => renderCommentCell(v),
    },
    {
      title: "Correction",
      key: "correct",
      width: 220,
      render: (_: unknown, row) => (
        <CorrectionCell row={row} reasons={reasons} pending={pending} />
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder="Filter article + comment…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          allowClear
          style={{ width: 320 }}
        />
        <Button
          size="small"
          type={hideOld ? "primary" : "default"}
          onClick={() => setHideOld((v) => !v)}
        >
          {hideOld ? "Showing recent only" : "Hide > 1 year old"}
        </Button>
      </Space>
      <Table<TransactionRow>
        columns={columns}
        dataSource={filtered}
        rowKey="id"
        // Pin the column header below the 64px AppShell top bar so the
        // bulb + user dropdown stay visible while scrolling.
        sticky={{ offsetHeader: 64 }}
        pagination={false}
        size="middle"
      />
    </div>
  );
};

function renderCommentCell(comment: string | null) {
  if (!comment) return "—";
  // Badge for the canonical correlation tags, via the shared parser
  // (``utils/txComment`` — mirrors the backend ``comment_tags`` grammar).
  // Known verbs (settle / correct) get a coloured badge + receipt tooltip;
  // the optional ``/modifier`` (settlement payment-method code) shows as a
  // second tag. Unknown verbs (incl. the legacy, no-longer-written
  // ``reenable``) and untagged comments render as raw text.
  const tag = parseTxComment(comment);
  const meta = tag ? TX_VERB_META[tag.verb] : undefined;
  if (tag && meta) {
    const tooltip = tag.modifier
      ? `Receipt ${tag.id} · ${tag.modifier}`
      : `Receipt ${tag.id}`;
    return (
      <Space size={4}>
        <Tag color={meta.color}>{meta.label}</Tag>
        {tag.modifier ? <Tag>{tag.modifier}</Tag> : null}
        <Tooltip title={tooltip}>
          <span>{tag.text || tag.id}</span>
        </Tooltip>
      </Space>
    );
  }
  return comment;
}

function CorrectionCell({
  row,
  reasons,
  pending,
}: {
  row: TransactionRow;
  reasons: CorrectionReasonRow[];
  pending: PendingBatch;
}) {
  const alreadyCorrected = row.corrected_at_correction_id !== null;
  const isCounter = row.is_counter_for_correction_id !== null;
  if (alreadyCorrected) {
    return <Tag color="orange">Corrected</Tag>;
  }
  if (isCounter) {
    return <Tag color="default">counter-transfer</Tag>;
  }

  const pickedReasonId = pending.pending.get(row.id);
  if (pickedReasonId !== undefined) {
    const reason = reasons.find((r) => r.id === pickedReasonId);
    return (
      <Tag
        color="processing"
        closable
        onClose={() => pending.remove(row.id)}
      >
        Will correct: {reason?.label ?? "—"}
      </Tag>
    );
  }

  // Ghost affordance: a faint Select that becomes a real dropdown on
  // hover/click. Operator should be able to scan the table and only
  // engage the affordance on a deliberate click.
  return (
    <Select
      size="small"
      placeholder="Correct…"
      style={{ width: 200, opacity: 0.65 }}
      variant="borderless"
      onChange={(rid) => pending.setReason(row.id, rid as number)}
      options={reasons
        .filter((r) => r.active)
        .map((r) => ({ value: r.id, label: r.label }))}
    />
  );
}
